import { useAppServices } from "hook/services";
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Grid,
  Alert,
  Snackbar,
  TextField,
  Card,
  CardContent,
  Divider
} from "@mui/material";

const Workflows = () => {
  const Service = useAppServices();
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflows, setSelectedWorkflows] = useState({});
  const [stripeConfig, setStripeConfig] = useState({
    stripe_api_key: "",
    stripe_public_key: "",
    stripeWebhookSecret: ""
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Convert workflow name to snake_case
  const convertToSnakeCase = (name) => {
    return name
      .toLowerCase()
      .replace(/\d+\.\s*/g, '') // Remove numbers and dots at start
      .replace(/\[.*?\]/g, '') // Remove text in brackets
      .replace(/[^\w\s]/g, '') // Remove special characters
      .trim()
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_'); // Remove multiple underscores
  };

  // Generate workflow types dynamically from available workflows
  const generateWorkflowTypes = () => {
    return workflows.map(workflow => ({
      key: convertToSnakeCase(workflow.name),
      label: `${workflow.name} (v${workflow.version}) ${workflow.status === 'draft' ? '(Draft)' : '(Published)'}`,
      originalId: workflow.id // Store original ID for reference
    }));
  };

  const getWorkflows = async () => {
    try {
      const { response } = await Service.super_admin_settings.list_workflows({});
      setWorkflows(response.data.workflows);
      console.log("Workflows fetched:", response.data.workflows);
    } catch (error) {
      console.error("Error fetching workflows:", error);
      setError("Failed to fetch available workflows");
    }
  };

  const getSetting = async () => {
    try {
      setFetching(true);
      const { response } = await Service.super_admin_settings.get();

      // Initialize selectedWorkflows with all available workflows
      const initialWorkflows = {};
      
      if (response.data.workflows) {
        // If we have existing saved workflows, use them
        Object.keys(response.data.workflows).forEach(key => {
          initialWorkflows[key] = response.data.workflows[key] || { 
            workflow_id: "", 
            workflow_name: "" 
          };
        });
      } else {
        // If no saved workflows, initialize with snake_case keys
        workflows.forEach(workflow => {
          const snakeCaseKey = convertToSnakeCase(workflow.name);
          initialWorkflows[snakeCaseKey] = { 
            workflow_id: "", 
            workflow_name: "" 
          };
        });
      }

      setSelectedWorkflows(initialWorkflows);

      if (response?.data) {
        setStripeConfig({
          stripe_api_key: response?.data?.stripe_api_key || "",
          stripe_public_key: response.data?.stripe_public_key || "",
          stripeWebhookSecret: response.data?.stripeWebhookSecret || ""
        });
      }
    } catch (error) {
      console.error("Error fetching workflow settings:", error);
      setError("Failed to fetch current workflow settings");
    } finally {
      setFetching(false);
    }
  };

  const handleWorkflowChange = (snakeCaseKey, selectedWorkflowId) => {
    const selected = workflows.find(w => w.id === selectedWorkflowId);
    setSelectedWorkflows(prev => ({
      ...prev,
      [snakeCaseKey]: {
        workflow_id: selected?.id || "",
        workflow_name: selected?.name || ""
      }
    }));
  };

  const handleStripeConfigChange = (field, value) => {
    setStripeConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveWorkflows = async () => {
    try {
      setLoading(true);
      const payload = {
        workflows: selectedWorkflows,
        stripe_api_key: stripeConfig.stripe_api_key,
        stripe_public_key: stripeConfig.stripe_public_key,
        stripeWebhookSecret: stripeConfig.stripeWebhookSecret
      };
      await Service.super_admin_settings.update({
        payload: payload
      });
      setSuccess(true);
    } catch (error) {
      console.error("Error saving workflows:", error);
      setError("Failed to save workflows");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      await getWorkflows();
      await getSetting();
    };
    fetchData();
  }, []);

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const workflowTypes = generateWorkflowTypes();

  return (
    <>
      <Typography variant="h4" component="h2" gutterBottom style={{ marginTop: "5rem", marginBottom: "2rem" }}>
        Workflow Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Stripe Configuration Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h3" gutterBottom>
                Stripe Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Configure your Stripe API keys for payment processing.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Stripe API Key"
                    value={stripeConfig.stripe_api_key}
                    onChange={(e) => handleStripeConfigChange('stripe_api_key', e.target.value)}
                    placeholder="sk_live_..."
                    variant="outlined"
                    margin="normal"
                    helperText="Your Stripe secret key (starts with sk_live_ or sk_test_)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Stripe Public Key"
                    value={stripeConfig.stripe_public_key}
                    onChange={(e) => handleStripeConfigChange('stripe_public_key', e.target.value)}
                    placeholder="pk_live_..."
                    variant="outlined"
                    margin="normal"
                    helperText="Your Stripe publishable key (starts with pk_live_ or pk_test_)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Stripe Webhook Key"
                    value={stripeConfig.stripeWebhookSecret}
                    onChange={(e) => handleStripeConfigChange('stripeWebhookSecret', e.target.value)}
                    placeholder="whe..."
                    variant="outlined"
                    margin="normal"
                    helperText="Stripe Webhook Key"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h3" gutterBottom>
                Workflow Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Assign workflows to different events. Total Workflows Available: {workflows.length}
              </Typography>

              <Grid container spacing={3}>
                {workflowTypes.map((type) => (
                  <Grid item xs={12} md={6} key={type.key}>
                    <FormControl fullWidth>
                      <InputLabel id={`${type.key}-label`}>{type.label}</InputLabel>
                      <Select
                        labelId={`${type.key}-label`}
                        value={selectedWorkflows[type.key]?.workflow_id || ""}
                        onChange={(e) => handleWorkflowChange(type.key, e.target.value)}
                        label={type.label}
                      >
                        <MenuItem value="">
                          <em>Select a workflow event</em>
                        </MenuItem>
                        {workflows.map(workflow => (
                          <MenuItem key={workflow.id} value={workflow.id}>
                            {workflow.name} (v{workflow.version}) {workflow.status === 'draft' ? '(Draft)' : '(Published)'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Button
            onClick={saveWorkflows}
            disabled={loading}
            fullWidth
            size="large"
            style={{ background: "#3F98EF", color: "white", marginTop: "2rem" }}
            variant="contained"
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Save All Settings'}
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={error !== null}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Settings saved successfully!
        </Alert>
      </Snackbar>
    </>
  );
}

export default Workflows;